"use client"

import React, { useContext, useState, useEffect } from "react"
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Paper,
} from "@mui/material"
import { Save as SaveIcon, Cancel as CancelIcon, CloudUpload as CloudUploadIcon } from "@mui/icons-material"
import axios from "axios"
import { AppContext } from "../../contexts/AppContext"
import { useMutation, useQueryClient } from "@tanstack/react-query"

function GiftEditor({ data, idPartner, onSave, onCancel }) {
  const { state } = useContext(AppContext)
  const isUpdating = data?.id > 0 // Determine whether this is an update or creation

  const [inputType, setInputType] = useState(data?.type || "")
  const [name, setName] = useState(data?.name || "")
  const [quantity, setQuantity] = useState(Number(data?.quantity || 0))
  const [points, setPoints] = useState(Number(data?.nbr_pts || 0))
  const [description, setDescription] = useState(data?.description || "")
  const [isActive, setIsActive] = useState(data?.isActive ?? true)
  const [file, setFile] = useState(null)

  // Use the first image in `images` array or fallback to `url`
  const initialPreview = data?.images?.length > 0 && data.images[0] ? data.images[0] : data?.url || ""
  const [preview, setPreview] = useState(initialPreview)

  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({ formData, idPartner, id }) => {
      const url = isUpdating
        ? `/api/v2/partner/${idPartner}/gifts/${id}` // Update existing gift
        : `/api/partner/store/${idPartner}/gifts/` // Create new gift

      const method = isUpdating ? "put" : "post" // Choose PUT or POST

      const response = await axios({
        method,
        url,
        data: formData,
        headers: {
          Authorization: state.token ? `Bearer ${state.token}` : "",
          "Content-Type": "multipart/form-data",
        },
      })

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["gift"])
      if (onSave) onSave()
    },
    onError: (error) => {
      console.error("Error saving gift:", error)
    },
  })

  useEffect(() => {
    console.log("Gift data loaded:", data)
  }, [data])

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile) {
      setFile(selectedFile)
      setPreview(URL.createObjectURL(selectedFile))
    }
  }

  const handleSave = () => {
    const formData = new FormData()
    formData.append("name", name)
    formData.append("description", description)
    formData.append("quantity", Number(quantity)) // Ensure it's a number
    formData.append("price", Number(points)) // Ensure it's a number
    formData.append("type", inputType)
    formData.append("isActive", isActive)

    if (file) {
      formData.append("file", file) // Only attach a new file if selected
    }

    mutation.mutate({
      formData,
      idPartner,
      id: data.id,
    })
  }

  // Custom file input styling
  const fileInputRef = React.useRef(null)
  const handleFileButtonClick = () => {
    fileInputRef.current.click()
  }

  return (
    <Card elevation={3} sx={{ maxWidth: 800, mx: "auto", overflow: "visible" }}>
      <CardHeader
        title={
          <Typography variant="h5" align="center" fontWeight="bold" sx={{ py: 1 }}>
            {isUpdating ? "Edit Gift" : "Create New Gift"}
          </Typography>
        }
        sx={{
          bgcolor: "primary.light",
          color: "primary.contrastText",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      />

      <CardContent sx={{ pt: 3, pb: 1 }}>
        <Grid container spacing={3}>
          {/* Left Column */}
          <Grid item xs={12} md={7}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <TextField
                fullWidth
                label="Gift Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                variant="outlined"
                InputProps={{ sx: { borderRadius: 1.5 } }}
              />

              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                variant="outlined"
                InputProps={{ sx: { borderRadius: 1.5 } }}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Quantity"
                    value={quantity}
                    onChange={(e) => setQuantity(Number(e.target.value))}
                    type="number"
                    variant="outlined"
                    InputProps={{ sx: { borderRadius: 1.5 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Points"
                    value={points}
                    onChange={(e) => setPoints(Number(e.target.value))}
                    type="number"
                    variant="outlined"
                    InputProps={{ sx: { borderRadius: 1.5 } }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={5}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, height: "100%" }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="select-type-label">Gift Type</InputLabel>
                <Select
                  labelId="select-type-label"
                  value={inputType}
                  label="Gift Type"
                  onChange={(e) => setInputType(e.target.value)}
                  sx={{ borderRadius: 1.5 }}
                >
                  <MenuItem value="credit">Credit</MenuItem>
                  <MenuItem value="physical">Physical</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />}
                label={
                  <Typography variant="body1">
                    Status:{" "}
                    <Typography
                      component="span"
                      color={isActive ? "success.main" : "text.secondary"}
                      fontWeight="medium"
                    >
                      {isActive ? "Active" : "Inactive"}
                    </Typography>
                  </Typography>
                }
                sx={{ ml: 0 }}
              />

              <Divider sx={{ my: 1 }} />

              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 1 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Gift Image
                </Typography>

                <Paper
                  elevation={0}
                  sx={{
                    width: 150,
                    height: 150,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                    mb: 2,
                    overflow: "hidden",
                    bgcolor: preview ? "transparent" : "action.hover",
                  }}
                >
                  {preview ? (
                    <img
                      src={preview || "/placeholder.svg"}
                      alt="Gift Preview"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <CloudUploadIcon sx={{ fontSize: 40, color: "text.secondary" }} />
                  )}
                </Paper>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />

                <Button
                  variant="outlined"
                  onClick={handleFileButtonClick}
                  startIcon={<CloudUploadIcon />}
                  sx={{ borderRadius: 1.5 }}
                >
                  {preview ? "Change Image" : "Upload Image"}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>

      <CardActions
        sx={{
          justifyContent: "space-between",
          p: 2.5,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "action.hover",
        }}
      >
        <Button
          variant="outlined"
          onClick={onCancel}
          color="error"
          startIcon={<CancelIcon />}
          sx={{ borderRadius: 1.5 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          color="primary"
          startIcon={<SaveIcon />}
          disabled={mutation.isPending}
          sx={{ borderRadius: 1.5 }}
        >
          {isUpdating ? "Update Gift" : "Create Gift"}
        </Button>
      </CardActions>
    </Card>
  )
}

export default GiftEditor

